import { Injectable } from '@angular/core';
import {
  CategoryDetails,
  UserData,
  UserDetails,
  receiptData,
} from './receipt.component';
import { environment } from 'src/environments/environment';
import { Observable, throwError } from 'rxjs';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError, mergeMap, map, tap, switchMap } from 'rxjs/operators';
import { UserStateService } from './detail/userState.service';

type Nullable<T> = T | null;
@Injectable({
  providedIn: 'root',
})
export class DataService {
  detailRecord: receiptData = {
    id: '',
    name: '',
    amount: '',
    category: '',
    createdBy: '',
    status: '',
    type: '',
    user_id:'',
    imageUrl:'',
    organization_id:'',

    trip:[],
  };
  constructor(
    private _httpClient: HttpClient,
    private userStateService: UserStateService
  ) {}

  setRecord(row: receiptData) {
    this.detailRecord = row;
  }

  getRecord(): receiptData {
    return this.detailRecord;
  }

  getMineBills():Observable<any>{
    const loggedInUserId = this.getUserIdFromLocal();
    console.debug(loggedInUserId)

    if (!loggedInUserId) {
      // If the user is not logged in, return an empty observable
      return of([]);
    }
    const baseUrl = `${environment.apiUrl}/bills`;
    const queryParams = `where=${encodeURIComponent(JSON.stringify({ user_id: loggedInUserId }))}&embedded=${encodeURIComponent(JSON.stringify({ status: 1, trip_id: 1,user_id:1,category_id:1,organization_id:1, }))}&max_results=10000000`;
    const url = `${baseUrl}?${queryParams}`;
    return this._httpClient.get(url);
  }
 
  getManagerBills(): Observable<any> {
    const loggedInUserId = this.getUserIdFromLocal();
    console.debug(loggedInUserId);
  
    if (!loggedInUserId) {
      return of([]);
    }
  
    const baseUrl = `${environment.apiUrl}/get-manager-bills`;
    const queryParams = new URLSearchParams({
      manager_id: loggedInUserId,
      embedded: JSON.stringify({
        status: 1,
        trip_id: 1,
        user_id: 1,
        category_id: 1,
        organization_id: 1,
      }),
      max_results: '10000000'
    }).toString();
  
    const url = `${baseUrl}?${queryParams}`;
    return this._httpClient.get<any>(url);
  }
  
  getAccountantBills(): Observable<any> {
    const loggedInUserId = this.getUserIdFromLocal();
    console.debug(loggedInUserId);
  
    if (!loggedInUserId) {
      return of([]);
    }
  
    const baseUrl = `${environment.apiUrl}/get-accountant-bills`;
    const queryParams = new URLSearchParams({
      accountant_id: loggedInUserId,
      embedded: JSON.stringify({
        status: 1,
        trip_id: 1,
        user_id: 1,
        category_id: 1,
        organization_id: 1,
      }),
      max_results: '10000000'
    }).toString();
  
    const url = `${baseUrl}?${queryParams}`;
    return this._httpClient.get<any>(url);
  }
 
  getLatestStatus(element:any): string {
    console.debug("element",element)
    let latestStatus = null;
    let latestDate = null;
    for (const key in element.status) {
      if (element.status.hasOwnProperty(key)) {
        const status = element.status[key];
        if (!latestDate || status.date > latestDate) {
          latestDate = status.date;
          latestStatus = status.value;
        }
      }
    }
    
    return latestStatus;
  }

  getUserRoles(userId: string): Observable<string[]> {
    return this._httpClient
      .get<any>(
        `${environment.apiUrl}/user_roles?where=%7B%22user_id%22%3A%22${userId}%22%7D&max_results=100000`
      )
      .pipe(
        map((apiResponse: any) => {
          // Filter roles based on the status being allowed
          const allowedRoles =
            apiResponse?._items
              .filter((item: any) => item.status === 'Allowed')
              .map((item: any) => item.role) || [];
          console.debug('apiResponse:::::::', allowedRoles);
          return allowedRoles;
        }),
        catchError((error) => {
          console.error('Error fetching user roles:', error);
          return of([]);
        })
      );
  }

  checkUserRoles(userId: string): Observable<any[]> {
    const apiUrl = `${environment.apiUrl}/user_roles?where={"user_id":"${userId}"}&max_results=10000000`;

    return this._httpClient.get<any>(apiUrl).pipe(
      switchMap((response: any) => {
        const roles = response?._items || [];
        console.debug('response', response?._items);

        if (!Array.isArray(roles)) {
          console.error('Unexpected response format for user roles:', roles);
          return of([]);
        }

        // Extract organization IDs from roles
        const organizationIds = roles.map((role) => role.organization_id);

        // Fetch organization details for each ID
        const organizationRequests = organizationIds.map((orgId) =>
          this._httpClient.get<any>(
            `${environment.apiUrl}/organizations/${orgId}`
          )
        );

        // Combine all organization requests
        return forkJoin(organizationRequests).pipe(
          map((organizationsResponse: any[]) => {
            if (!Array.isArray(organizationsResponse)) {
              console.error(
                'Unexpected response format for organization details:',
                organizationsResponse
              );
              return roles;
            }

            // Assuming 'organizations' is an array of objects with a 'name' property
            const organizations = organizationsResponse;

            // Update roles with organization names
            const rolesWithOrgNames = roles.map((role, index) => {
              const organization = organizations.find(
                (org) => org._id === role.organization_id
              );
              return {
                ...role,
                organization_name: organization ? organization.name : undefined,
              };
            });

            return rolesWithOrgNames;
          }),
          catchError((error) => {
            console.error('Error fetching organization details:', error);
            return of(roles); // Return roles without organization details in case of an error
          })
        );
      }),
      catchError((error) => {
        console.error('Error fetching user roles:', error);
        return of([]);
      })
    );
  }
  getUserDetailsWithUserName(
    userRoles: { orgId: string; orgName: string; roles: any[] }[]
  ): Observable<{ orgId: string; orgName: string; roles: any[] }[]> {
    const userRolesWithUserDetailsRequests: Observable<{
      orgId: string;
      orgName: string;
      roles: any[];
    }>[] = userRoles.map(
      (orgData: { orgId: string; orgName: string; roles: any[] }) => {
        const userDataRequests: Observable<any>[] = orgData.roles.map(
          (userRole: any) =>
            this._httpClient
              .get<any>(`${environment.apiUrl}/users/${userRole.user_id}`)
              .pipe(
                catchError((error) => {
                  console.error('Error fetching user details:', error);
                  return of(null); // Return null for failed requests
                })
              )
        );

        return forkJoin(userDataRequests).pipe(
          map((userDetails: any[]) => {
            const updatedRoles = orgData.roles.map((role, index) => {
              const userDetail = userDetails[index];
              return {
                ...role,
                username: userDetail ? userDetail.username : '',
              };
            });

            return {
              orgId: orgData.orgId,
              orgName: orgData.orgName,
              roles: updatedRoles,
            };
          })
        );
      }
    );

    return forkJoin(userRolesWithUserDetailsRequests);
  }
  getUserRolesWithOrgNames(
    userId: string
  ): Observable<{ orgId: string; orgName: string; roles: any[] }[]> {
    // Fetch organizations where admin_id matches userId
    return this._httpClient
      .get<{ _items: any[] }>(
        `${environment.apiUrl}/organizations?where=%7B%22admin_id%22%3A%22${userId}%22%7D&max_results=10000000`
      )
      .pipe(
        mergeMap((orgApiResponse: { _items: any[] }) => {
          if (Array.isArray(orgApiResponse._items)) {
            const orgsDataRequests: Observable<{
              orgId: string;
              orgName: string;
              roles: any[];
            }>[] = orgApiResponse._items.map((orgItem: any) => {
              // Fetch user roles based on organization ID
              return this._httpClient
                .get<any>(
                  `${environment.apiUrl}/user_roles?where=%7B%22organization_id%22%3A%22${orgItem._id}%22%7D&max_results=1000000`
                )
                .pipe(
                  map((userRolesResponse: any) => ({
                    orgId: orgItem._id,
                    orgName: orgItem.name,
                    roles: userRolesResponse._items || [],
                  }))
                );
            });

            return forkJoin(orgsDataRequests);
          } else {
            console.error(
              'Organizations API Response Error: Expected an array inside _items, but received:',
              orgApiResponse
            );
            return of([] as { orgId: string; orgName: string; roles: any[] }[]);
          }
        }),
        catchError((error) => {
          console.error('Error fetching Org data for User:', error);
          return of([] as { orgId: string; orgName: string; roles: any[] }[]);
        })
      );
  }

  getUserDetails(userId: any): Observable<any | null> {
    return this._httpClient
    .get<{ userId: any; name: any }>(environment.apiUrl + `/users/${userId}?embedded=${encodeURIComponent('{"bank_details":1}')}`)
      .pipe(
        tap((response: any) => console.debug('UserDetails:', response)),
        catchError((error) => {
          if (error.status === 404) {
            // console.de(`User with ID ${userId} not found.`);
            return of(null); // Return null for 404 errors
          }
          console.error('Error fetching user details:', error);
          return throwError(error);
        })
      );
  }
  acceptRequest(
    reqId: string,
    updateData: any,
    reqEtag: string
  ): Observable<any> {
    const url = `${environment.apiUrl}/user_roles/${reqId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': reqEtag,
    });
    return this._httpClient.patch(url, updateData, { headers });
  }
  updateBill(
    billId: string,
    updatedData: any,
    billEtag: string
  ): Observable<any> {
    const url = `${environment.apiUrl}/bills/${billId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': billEtag,
    });

    return this._httpClient.patch(url, updatedData, { headers });
  }

  updateBulkBill(
    updatedData: any,
  ): Observable<any> {
    const url = `${environment.apiUrl}/bulk_update_bill_status`;   
   return this._httpClient.patch(url, updatedData);
  }
  updateParticipants(
    updatedData: any,
  ): Observable<any> {
    const url = `${environment.apiUrl}/update_trip_participants`;   
   return this._httpClient.patch(url, updatedData);
  }

  updateBillStatus(
    billStatusId: string,
    updatedStatusData: any,
    billStatusEtag: string
  ): Observable<any>{
    console.debug(billStatusId,"::Etag",billStatusEtag,"::updateData:",updatedStatusData)
    const url = `${environment.apiUrl}/bill_status/${billStatusId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': billStatusEtag,
    });
    return this._httpClient.patch(url, updatedStatusData, { headers });
  }
  updateUserDetail(
    userId: string,
    updatedData: any,
    userEtag: string
  ): Observable<any> {
    const url = `${environment.apiUrl}/users/${userId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': userEtag,
    });

    return this._httpClient.patch(url, updatedData, { headers });
  }

  updateBankDetails(
    bankId: string,
    updatedData: any,
    bankEtag: string
  ): Observable<any>{
    const url = `${environment.apiUrl}/bank_details/${bankId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': bankEtag,
    });
    return this._httpClient.patch(url, updatedData, { headers });
  }

  addBankDetails(updateData:any): Observable<any>{
    const url = `${environment.apiUrl}/bank_details`;
    return this._httpClient.post(url, updateData);
  }

  updateBillTrip(
    billId: string,
    updatedData: any,
    billEtag: string
  ): Observable<any> {
    const url = `${environment.apiUrl}/bills/${billId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': billEtag,
    });

    return this._httpClient.patch(url, updatedData, { headers });
  }
  updatePassDetail(
    updatedData: any
  ): Observable<any> {
    const url = `${environment.apiUrl}/change-password`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._httpClient.patch(url, updatedData, { headers });
  }
  requestUserRole(updatedData: any): Observable<any> {
    const url = `${environment.apiUrl}/user_roles`;
    return this._httpClient.post(url, updatedData);
  }
  postUserUnderManager(updatedData: any): Observable<any>{
    const url = `${environment.apiUrl}/user_managers`;
    return this._httpClient.post(url, updatedData);
  }

  postCategory(updatedData: any): Observable<any>{
    const url = `${environment.apiUrl}/categories`;
    return this._httpClient.post(url, updatedData);
  }
  updateUserRole(updatedData: any): Observable<any> {
    const url = `${environment.apiUrl}/update-roles`;
    return this._httpClient.patch(url, updatedData);
  }
  terminateRole(updatedData: any): Observable<any> {
    const url = `${environment.apiUrl}/deactivate-employee`;
    return this._httpClient.patch(url, updatedData);
  }

  activateRole(updatedData: any): Observable<any> {
    const url = `${environment.apiUrl}/activate-employee`;
    return this._httpClient.patch(url, updatedData);
  }
  deleteBill(billId: string, billEtag: string): Observable<any> {
    const url = `${environment.apiUrl}/bills/${billId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': billEtag,
    });
    return this._httpClient.delete(url, { headers });
  }

    getImageUrlObservable(file: string): any {
    const apiUrl = environment.apiUrl + file;
    return apiUrl;
  }

  getBlockedDetails(user_id:string,org_id:string){
    const url = `${environment.apiUrl}/user_roles?where={"user_id":"${user_id}","organization_id":"${org_id}"}&max_results=10000000`;
    return this._httpClient.get(url);
  }
  getUserBills(userId: string, orgId: string): Observable<any> {
    const url = `${environment.apiUrl}/bills?where={"user_id":"${userId}","organization_id":"${orgId}"}&embedded={"user_id":1,"trip_id":1,"organization_id":1,"category_id":1,"status":1}&max_results=10000000`;
    return this._httpClient.get(url);
  }

  getTripBills(tripId:string): Observable<any>{
    const url = `${environment.apiUrl}/bills?where={"trip_id":"${tripId}"}&embedded={"user_id":1,"trip_id":1,"organization_id":1,"category_id":1,"status":1}&max_results=10000000`;
    return this._httpClient.get(url);
  }
 
  getCurrencies(): Observable<any> {
    const url = `${environment.apiUrl}/currencies`;
    return this._httpClient.get(url);
  }
  getParticularUserRole(userId: any): Observable<any> {
    const url = `${environment.apiUrl}/user_roles?where=%7B%22user_id%22%3A%22${userId}%22%7D`;
    return this._httpClient.get(url);
  }

  getBillPaymentDetails(userId: any): Observable<any> {
    const url = `${environment.apiUrl}/bill_payment?where={"paid_by":"${userId}"}&embedded={"paid_by":1,"bill_id":1,"bill_id.user_id":1}&max_results=10000000`;
    return this._httpClient.get(url);
  }
  getNotificationsForUser(userId: any): Observable<any> {
    const url = `${environment.apiUrl}/notifications?where={"user_id":"${userId}"}&embedded={"user_id":1,"requesting_org":1,"request_send_to_user":1}&max_results=10000000`;
    return this._httpClient.get(url);
  }
  getUsers(): Observable<any> {
    const url = `${environment.apiUrl}/users?max_results=10000000`;
    return this._httpClient.get(url);
  }

  removeTripBill(
    billId: string,
    updatedData: any,
  ): Observable<any> {
    const url = `${environment.apiUrl}/remove-trip`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._httpClient.patch(url, updatedData, { headers });
  }


  getUserRoleForOrg(org_id:any){
    const url =   `${environment.apiUrl}/user_roles?where=%7B%22organization_id%22%3A%22${org_id}%22%2C%22status%22%3A%22Allowed%22%7D&embedded={"user_id":1}`;
    return this._httpClient.get(url);
  }
  fetchRoleParticularOrg(user_id:string,org_id:string){
    const url =  `${environment.apiUrl}/user_roles?where={"user_id":"${user_id}","organization_id":"${org_id}","status":"Allowed"}`
    ;
    return this._httpClient.get(url);
  }
  fetchEmpForMan(user_id:string,org_id:string){
    const url =  `${environment.apiUrl}/user_managers?where={"manager_id":"${user_id}","organization_id":"${org_id}"}&embedded={"user_id":1}`
    ;
    return this._httpClient.get(url);
  }
  fetchRoleParticularOrgNoUser(user_id:string,org_id:string){
    const url =  `${environment.apiUrl}/user_roles?where={"role":"Employee","organization_id":"${org_id}","status":"Allowed","deactivate_role":false}&embedded={"user_id":1}`
    ;
    return this._httpClient.get(url);
  }
  getOrgForUser(user_id:any){
    const url = `${environment.apiUrl}/user_roles/?where=%7B%22user_id%22%3A%22${user_id}%22%7D&embedded={"organization_id":1}`;
    return this._httpClient.get(url);
  }

  getCategories(){
    const url = `${environment.apiUrl}/categories?max_results=10000000`;
    return this._httpClient.get(url);
  }

  getTrip(trip_id:any){
    const url = `${environment.apiUrl}/trips/${trip_id}?embedded={"organization_id":1}`;
    return this._httpClient.get(url);
  }
  updateTripDetail(
    tripId: string,
    updatedData: any,
    tripEtag: string
  ): Observable<any> {
    const url = `${environment.apiUrl}/trips/${tripId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': tripEtag,
    });

    return this._httpClient.patch(url, updatedData, { headers });
  }

  getOrgs(admin_id: any): Observable<any> {
    const url = `${environment.apiUrl}/organizations/?where=%7B%22admin_id%22%3A%22${admin_id}%22%7D`;
    return this._httpClient.get(url);
  }
  getTrips(user_id: any): Observable<any> {
    const url = `${environment.apiUrl}/trips/?where={"created_by":"${user_id}"}&embedded={"org_id":1,"created_by":1}`;
    return this._httpClient.get(url);
  }

  getAllTrips(): Observable<any> {
    const url = `${environment.apiUrl}/trips?embedded={"org_id":1,"created_by":1}&max_results=10000000`;
    return this._httpClient.get(url);
  }

  getBill(bill_id: any): Observable<any> {
    const url = `${environment.apiUrl}/bills/${bill_id}?embedded={"status":1,"status.Paid.payment_details":1,"status.Approved.by":1,"status.Paid.by":1,"status.Paid.by":1,"status.Rejected.by":1,"status.Review.by":1,"status.In-Progress.by":1,"status.Updated.by":1}&max_results=10000000`;
    return this._httpClient.get(url);
  }

  deleteTrips(
    tripId: string,
    tripEtag: string
  ): Observable<any> {
    const url = `${environment.apiUrl}/trips/${tripId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': tripEtag,
    });
    return this._httpClient.delete(url, { headers });
  }

  postTrips(updatedData: any): Observable<any> {
    const url = `${environment.apiUrl}/trips`;
    return this._httpClient.post(url, updatedData);
  }
  getOrgInfo(org_id:string){
    const url = `${environment.apiUrl}/organizations/${org_id}`;
    return this._httpClient.get(url);
  }
  getAllOrgs(): Observable<any> {
    const url = `${environment.apiUrl}/organizations?max_results=10000000`;
    return this._httpClient.get(url);
  }
  deleteRequest(
    reqId: string,
    reqEtag: string
  ): Observable<any> {
    const url = `${environment.apiUrl}/user_roles/${reqId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': reqEtag,
    });
    return this._httpClient.delete(url, { headers });
  }

  deleteEmpMan(
    reqId: string,
    reqEtag: string
  ): Observable<any> {
    const url = `${environment.apiUrl}/user_managers/${reqId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': reqEtag,
    });
    return this._httpClient.delete(url, { headers });
  }
  deleteRole(reqId: string, reqEtag: string): Observable<any> {
    const url = `${environment.apiUrl}/user_roles/${reqId}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'If-Match': reqEtag,
    });
    return this._httpClient.delete(url, { headers });
  }

  getEtagForBill(billStatusId: string){
    const url = `${environment.apiUrl}/bill_status/${billStatusId}`;
    return this._httpClient.get(url);
  }

  getBillDetails(billId: string): Observable<any> {
    return this._httpClient
      .get<any>(environment.apiUrl + `/bills/${billId}?embedded={"status":1,"organization_id":1,"trip_id":1}`)
      .pipe(
        tap((response: any) => console.debug('BillDetails:', response)),
        switchMap((billDetails: any) => {
          // Fetch user details and category details in parallel
          return forkJoin({
            user: this.getUserDetails(billDetails.user_id),
            category: this.getBillCategoryDetails(billDetails.category_id),
            bill: of(billDetails), // Wrap billDetails in an observable for consistency
          });
        }),
        catchError((error) => {
          console.error('Error fetching bill details:', error);
          return throwError(error);
        })
      );
  }
  getBillCategoryDetails(categoryId: any): Observable<CategoryDetails | null> {
    return this._httpClient
      .get<{ categoryId: any; name: any }>(
        environment.apiUrl + `/categories/${categoryId}`
      )
      .pipe(
        tap((response: any) => console.debug('CategoryDetails:', response)),
        catchError((error) => {
          if (error.status === 404) {
            // console.warn(`Category with ID ${categoryId} not found.`);
            return of(null); // Return null for 404 errors
          }
          console.error('Error fetching category details:', error);
          return throwError(error);
        })
      );
  }

  getUserIdFromLocal(): string {
    var storedResponse = localStorage.getItem('LoginResponse');
    if (storedResponse !== null) {
      var parsedResponse = JSON.parse(storedResponse);
      var userId = parsedResponse._items[0].user_id;
      console.debug(userId + ' localtorage');
      return userId;
    } else {
      console.error('Stored response is null');
      return '';
    }
  }

  uploadImage(updatePayment: any): Observable<any> {
    const url = `${environment.apiUrl}/bill_payment`;
    return this._httpClient.post(url, updatePayment);
  }

}
